import { RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { Chip, Tooltip, makeStyles } from '@material-ui/core';

import throttle from 'lodash/throttle';

import { LabelDefinition } from '../../../hooks/useLabelsForResource';

function useResponsiveVisibleCount(
  containerRef: RefObject<HTMLDivElement>,
  measureRef: RefObject<HTMLDivElement>,
  itemCount: number,
  expanded: boolean,
): number | undefined {
  const [maxVisible, setMaxVisible] = useState<number | undefined>(undefined);

  const throttledCalculate = useMemo(
    () =>
      throttle(() => {
        const container = containerRef.current;
        const measureContainer = measureRef.current;
        if (!container || !measureContainer) return;

        const containerWidth = container.offsetWidth;
        if (containerWidth <= 0) return;

        const chips = measureContainer.children;
        const gap = 4; // theme.spacing(0.5)
        const moreChipWidth = 80; // Approximate width for "+ X labels" chip

        let usedWidth = 0;
        let visibleCount = 0;

        for (let i = 0; i < chips.length; i++) {
          const chipWidth = (chips[i] as HTMLElement).offsetWidth;
          const widthWithGap = i === 0 ? chipWidth : chipWidth + gap;
          const remainingLabels = chips.length - i - 1;
          const needsMoreChip = remainingLabels > 0;
          const availableWidth = needsMoreChip
            ? containerWidth - moreChipWidth - gap
            : containerWidth;

          if (usedWidth + widthWithGap <= availableWidth) {
            usedWidth += widthWithGap;
            visibleCount++;
          } else {
            break;
          }
        }

        setMaxVisible(visibleCount > 0 ? visibleCount : 1);
      }, 50),
    [containerRef, measureRef],
  );

  useEffect(() => {
    if (expanded || !containerRef.current || !measureRef.current) {
      setMaxVisible(undefined);
      return undefined;
    }

    throttledCalculate();

    const resizeObserver = new ResizeObserver(() => {
      throttledCalculate();
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      throttledCalculate.cancel();
    };
  }, [expanded, itemCount, throttledCalculate, containerRef, measureRef]);

  return maxVisible;
}

const useStyles = makeStyles(theme => ({
  wrapper: {
    width: '100%',
    minWidth: 0,
    position: 'relative',
  },
  root: {
    display: 'flex',
    gap: theme.spacing(0.5),
    width: '100%',
  },
  rootCollapsed: {
    flexWrap: 'nowrap',
    overflow: 'hidden',
  },
  rootExpanded: {
    flexWrap: 'wrap',
  },
  chip: {
    height: 24,
    fontSize: '0.75rem',
    flexShrink: 0,
  },
  measureContainer: {
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.5),
  },
}));

type LabelKeyValue = {
  key: string;
  value?: string | null;
};

export type LabelChipsProps = {
  labels: LabelKeyValue[];
  labelDefinitions: LabelDefinition[];
  expanded?: boolean;
  onExpandClick?: () => void;
  onLabelClick?: (labelKey: string, value: string) => void;
};

export const LabelChips = ({
  labels,
  labelDefinitions,
  expanded = false,
  onExpandClick,
  onLabelClick,
}: LabelChipsProps) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const definitionMap = new Map(labelDefinitions.map(def => [def.id, def]));

  const displayLabels = labels.flatMap(label => {
    const definition = definitionMap.get(label.key);
    if (!definition || !label.value) {
      return [];
    }
    return [{ key: label.key, value: label.value, definition }];
  });

  const maxVisible = useResponsiveVisibleCount(
    containerRef,
    measureRef,
    displayLabels.length,
    expanded,
  );

  if (displayLabels.length === 0) {
    return null;
  }

  const shouldLimit = maxVisible !== undefined && !expanded;
  const visibleLabels = shouldLimit
    ? displayLabels.slice(0, maxVisible)
    : displayLabels;
  const hiddenCount = shouldLimit
    ? Math.max(0, displayLabels.length - maxVisible)
    : 0;

  return (
    <div ref={containerRef} className={classes.wrapper}>
      {/* Hidden container for measuring chip widths */}
      <div ref={measureRef} className={classes.measureContainer}>
        {displayLabels.map(label => (
          <Chip
            key={label.key}
            label={label.value}
            size="small"
            className={classes.chip}
          />
        ))}
      </div>

      {/* Visible container */}
      <div
        className={`${classes.root} ${expanded ? classes.rootExpanded : classes.rootCollapsed}`}
      >
        {visibleLabels.map(label => (
          <Tooltip
            key={label.key}
            title={`${label.definition.displayName}: ${label.value}`}
          >
            <Chip
              label={label.value}
              size="small"
              className={classes.chip}
              clickable={!!onLabelClick}
              onClick={
                onLabelClick
                  ? e => {
                      e.stopPropagation();
                      onLabelClick(label.key, label.value);
                    }
                  : undefined
              }
             data-track-source-anchor="7041090ddd;label-chips-chip"/>
          </Tooltip>
        ))}
        {hiddenCount > 0 && (
          <Chip
            label={`+ ${hiddenCount} ${hiddenCount === 1 ? 'label' : 'labels'}`}
            size="small"
            className={classes.chip}
            clickable={!!onExpandClick}
            onClick={
              onExpandClick
                ? e => {
                    e.stopPropagation();
                    onExpandClick();
                  }
                : undefined
            }
           data-track-source-anchor="e4f732c0ea;label-chips-chip-2"/>
        )}
      </div>
    </div>
  );
};
